import { Box, Card, CardContent, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import {
    CreateFormQuestionOptionBody,
    MoveFormQuestionOptionBody,
    UpdateFormQuestionOptionBody,
} from '../../../services/apiSLTP/qhse/forms/formQuestionOptions';
import { FormQuestionWithOptions, UpdateFormQuestionBody } from '../../../services/apiSLTP/qhse/forms/formQuestions';
import Icon from '../../ui/Icon';
import AddQuestionButtons from './AddQuestionButtons';
import QuestionEdit from './QuestionEdit';
import QuestionTypeViewer from './options/QuestionTypeViewer';

interface QuestionItemProps {
    question: FormQuestionWithOptions;
    sectionIndex: number;
    sectionsLength: number;
    questionsLength: number;
    index: number;
    moveQuestion: (fromSectionIndex: number, fromIndex: number, toSectionIndex: number, toIndex: number) => void;
    selectedId: string | null;
    setSelectedId: (id: string | null) => void;
    formColor: string;
    number: number;
    onDrag(isDragging: boolean, itemId: string | null): void;
    onAddQuestion: (sectionId: string, type: number, order: number, setSelectedId: (id: string | null) => void) => void;
    isGlobalDragging: boolean;
    draggingItemId: string | null;
    sectionId: string;
    onUpdateQuestion: (questionId: string, data: UpdateFormQuestionBody) => void;
    onDuplicateQuestion: (questionId: string, setSelectedId: (id: string | null) => void) => void;
    onDeleteQuestion: (questionId: string) => void;
    onAddQuestionOption: (data: CreateFormQuestionOptionBody) => void;
    onDeleteQuestionOption: (optionId: string) => void;
    onUpdateQuestionOption: (optionId: string, data: UpdateFormQuestionOptionBody) => void;
    onMoveQuestionOption: (optionId: string, data: MoveFormQuestionOptionBody) => void;
    onMoveQuestion: (questionId: string, toSectionId: string, order: number) => void;
    disabled?: boolean;
}

const QuestionItem: React.FC<QuestionItemProps> = ({
    question,
    sectionIndex,
    sectionsLength,
    questionsLength,
    index,
    moveQuestion,
    selectedId,
    setSelectedId,
    formColor,
    number,
    onDrag,
    isGlobalDragging,
    draggingItemId,
    onAddQuestion,
    sectionId,
    onUpdateQuestion,
    onDuplicateQuestion,
    onDeleteQuestion,
    onAddQuestionOption,
    onDeleteQuestionOption,
    onUpdateQuestionOption,
    onMoveQuestionOption,
    onMoveQuestion,
    disabled = false,
}) => {
    const { t } = useTranslation();
    const isSelected = selectedId === question._id;
    const ref = useRef<HTMLDivElement>(null);
    const dragHandleRef = useRef<HTMLDivElement>(null);
    const [hover, setHover] = useState(false);
    const [title, setTitle] = useState(question.title || '');
    const [required, setRequired] = useState(question.required || false);

    const [{ isDragging }, drag, preview] = useDrag({
        type: 'QUESTION',
        canDrag: !disabled,
        item: () => ({ index, sectionIndex, id: question._id }),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });
    useEffect(() => {
        if (ref.current) preview(ref.current);
    }, [preview]);
    drag(dragHandleRef);

    useEffect(() => {
        if (isDragging) onDrag(true, question._id);
    }, [isDragging]);

    const [, drop] = useDrop({
        accept: 'QUESTION',
        hover: (item: { index: number; sectionIndex: number; id: string }, monitor) => {
            if (!ref.current) return;

            if (item.id === question._id) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (item.sectionIndex === sectionIndex && item.index < index && hoverClientY < hoverMiddleY) return;
            if (item.sectionIndex === sectionIndex && item.index > index && hoverClientY > hoverMiddleY) return;

            moveQuestion(item.sectionIndex, item.index, sectionIndex, index);

            item.index = index;
            item.sectionIndex = sectionIndex;
        },
        drop: (item, monitor) => {
            onDrag(false, null);
        },
    });

    drop(ref);

    return (
        <>
            <Card
                ref={ref}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                sx={{
                    borderLeft: isSelected ? `6px solid ${formColor}` : '6px solid transparent',
                    cursor: isSelected ? 'default' : 'pointer',
                    bgcolor: isSelected || isGlobalDragging ? 'action.selected' : 'transparent',

                    borderRadius: '4px',
                    visibility: isDragging ? 'hidden' : 'visible',
                    transform: 'unset',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: isSelected || isGlobalDragging ? 'action.selected' : 'action.hover',
                    },
                    opacity: isGlobalDragging && draggingItemId === question._id ? 0 : 1,
                }}
                onClick={() => setSelectedId(question._id)}
                elevation={0}
            >
                <CardContent sx={{ p: 1, gap: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" flexDirection={'row'} alignItems="center" justifyContent={'center'}>
                        <Box
                            ref={dragHandleRef}
                            sx={{
                                cursor: 'move',
                                opacity: hover || isSelected ? 1 : 0,
                                transition: 'opacity 0.2s',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Icon name="dragIndicator" sx={{ rotate: '90deg' }} />
                        </Box>
                    </Box>

                    {!isSelected ? (
                        <Box>
                            <Typography variant="h6" component="div">
                                {`${number}. ${title || ''}`}
                                {required && (
                                    <Typography variant="h6" component="span" color="error">
                                        {' *'}
                                    </Typography>
                                )}
                            </Typography>
                            <QuestionTypeViewer question={question} />
                        </Box>
                    ) : (
                        <QuestionEdit
                            question={question}
                            questionsLength={questionsLength}
                            number={number}
                            index={index}
                            title={title}
                            required={required}
                            onTitleChange={(text) => {
                                setTitle(text);
                                onUpdateQuestion(question._id, { title: text });
                            }}
                            onRequiredChange={(checked) => {
                                setRequired(checked);
                                onUpdateQuestion(question._id, { required: checked });
                            }}
                            onDuplicate={() => onDuplicateQuestion(question._id, setSelectedId)}
                            onDelete={() => onDeleteQuestion(question._id)}
                            onMoveQuestion={onMoveQuestion}
                            onAddQuestionOption={onAddQuestionOption}
                            onDeleteQuestionOption={onDeleteQuestionOption}
                            onUpdateQuestionOption={onUpdateQuestionOption}
                            onMoveQuestionOption={onMoveQuestionOption}
                        />
                    )}
                </CardContent>
            </Card>
            {!disabled &&
                ((isSelected && draggingItemId !== question._id) ||
                    (selectedId === null && sectionsLength === sectionIndex + 1 && questionsLength === index + 1)) && (
                    <AddQuestionButtons sectionId={sectionId} index={index} onAddQuestion={onAddQuestion} setSelectedId={setSelectedId} />
                )}
        </>
    );
};

export default QuestionItem;
